import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { ShieldCheck, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * /reset-pin?token=XYZ — landing page from the reset email. Sets a new 6-digit
 * PIN against the one-time token. Backend rejects weak / sequential / common PINs.
 */
export default function ResetPinPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pin !== pin2) { setError('PINs do not match.'); return; }
    if (!/^\d{6}$/.test(pin)) { setError('PIN must be 6 digits.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/pin/reset', { reset_token: token, pin });
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center px-6 py-12">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <AlertCircle size={32} className="text-amber-500 mx-auto mb-3" />
          <p className="text-sm text-slate-700">This reset link is missing its token. Please tap the link in your email again.</p>
          <Link to="/forgot-pin" className="inline-block mt-4 text-sm text-sky-600 font-medium">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 flex items-center justify-center px-6 py-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <ShieldCheck size={36} className="text-emerald-500 mx-auto mb-3" />
          <h1 className="text-2xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Set your new PIN</h1>
          <p className="text-sm text-slate-500 mt-1">Pick 6 digits you'll remember. Avoid 1234, your birthday, or all-same digits.</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          {done ? (
            <div className="text-center py-4" data-testid="reset-pin-done">
              <CheckCircle size={32} className="text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-slate-700 font-medium">All set. Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="text-xs text-red-600 bg-red-50 p-2 rounded-lg" data-testid="reset-pin-error">{error}</div>}
              <PinField label="New PIN" value={pin} setValue={setPin} testid="reset-pin-input" autoFocus />
              <PinField label="Confirm PIN" value={pin2} setValue={setPin2} testid="reset-pin-confirm" />
              <button type="submit" disabled={loading || pin.length !== 6 || pin2.length !== 6}
                className="w-full rounded-full py-3 bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-pin-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {loading ? 'Saving…' : 'Set my PIN'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PinField({ label, value, setValue, testid, autoFocus }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</label>
      <input
        type="password" inputMode="numeric" maxLength={6} autoFocus={autoFocus}
        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-base text-center font-mono tracking-[0.5em] outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
        value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="••••••" data-testid={testid}
      />
    </div>
  );
}
